import {
	Pallet_t,
	PlaneDimensions,
	getPalletDimensions,
	Box_t,
	BoxPosition_t,
	auto_all_rotated,
	auto_all_forward,
	getBoxPositionsBoundingBoxCenter,
	AutoLayerParams,
	BoxDimensions,
	PalletDimensions,
	Layout_t,
	MultiPickType
} from 'blackbox';
import { PackResult } from 'libapi';
import { LayoutBounds, ModelRect } from './Layouts/utilities/types';
import { roundToNearestDecimal } from '../shared/shared';


export interface Rect2d {
	x: number;
	y: number;
	width: number;
	height: number;
}

export function generateMultipickDivLines(
	pRect: Rect2d,
	pMultipick: MultiPickType,
	pMultipickAlongWidth: boolean,
	pRotated: boolean,
	pWidth?: number
): Rect2d[] {
	const lines: Rect2d[] = [];

	const lNumDivs = pMultipick + 1;
	let lMajorDim = pRotated ? pRect.width : pRect.height;
	let lMinorDim = pRotated ? pRect.height : pRect.width;

	if (pMultipickAlongWidth) {
		[lMajorDim, lMinorDim] = [lMinorDim, lMajorDim];
	}

	const lDivGap = lMajorDim / lNumDivs;
	const lSlitWidth = pWidth ? pWidth : lMinorDim * 0.01;

	const lAligned =
		(!pRotated && !pMultipickAlongWidth) || (pRotated && pMultipickAlongWidth);
	for (let i = 1; i < lNumDivs; i++) {
		const lXOffset = lAligned ? 0 : lDivGap * i;
		const lYOffset = lAligned ? lDivGap * i : 0;
		const lSlit = {
			x: pRect.x + lXOffset,
			y: pRect.y + lYOffset,
			width: lAligned ? lMinorDim : lSlitWidth,
			height: lAligned ? lSlitWidth : lMinorDim,
		};
		lines.push(lSlit);
	}

	return lines;
}

export interface MultipickGripperLines {
  circles: React.SVGProps<SVGCircleElement>[];
  line: React.SVGProps<SVGLineElement>;
}

export function generateMultipickGripperLines(
	rect: Rect2d,
	multipick: MultiPickType,
	multipickAlongWidth: boolean,
	rotated: boolean,
): MultipickGripperLines | null {
  if (multipick === MultiPickType.None) return null;

  const numberOfBoxes = multipick + 1;
  const isFavouringLengthDimension = (multipickAlongWidth && !rotated) || (!multipickAlongWidth && rotated);
  let widthOfBox = isFavouringLengthDimension ? rect.width / numberOfBoxes : rect.width;
  let heightOfBox = isFavouringLengthDimension ? rect.height : rect.height / numberOfBoxes;

  
  const circles: { x: number, y: number }[] = [
    {
      x: rect.x + widthOfBox / 2,
      y: rect.y + heightOfBox / 2
    }
  ];

  for (let i = 1; i <= multipick; i++) {
    circles.push(isFavouringLengthDimension ? {
      x: circles[i-1].x + widthOfBox,
      y: circles[i-1].y
    } : {
      x: circles[i-1].x,
      y: circles[i-1].y + heightOfBox
    })
  }

  return {
    circles: circles.map(circle => ({
      cx: circle.x,
      cy: circle.y
    })),
    line: {
      x1: circles[0].x,
      y1: circles[0].y,
      x2: circles[circles.length - 1].x,
      y2: circles[circles.length - 1].y,
    }
  }
}

export interface Rect {
	x: number;
	y: number;
	width: number;
	height: number;
	fill: string;
	stroke: string;
	strokeWidth: number | string;
	offset?: any;
	transform?: string;
}

export enum LayoutTypes {
	AllForward = 'All Forward',
	AllRotated = 'All Rotated',
	Optimized = 'Optimized',
}

interface ModelData {
	w: number;
	l: number;
	scale: number;
	norm: number;
	cx: number;
	topX: number;
	topY: number;
	size: number;

	/* dimensions with overhang */
	overhangData: {
		width: number;
		length: number;
		x: number;
		y: number;
	};
}

/**
 * Retrieve properties of the pallet in px from actual dimensions
 * @param pallet the pallet used to create a layout
 * @param size the size in px of the (square) div element to fix the pallet
 * @param overhang the size of the overhang (an extra allowance strip around the pallet) in mm
 * @returns the dimensions, offset and size of the pallet (with overhang) in px
 */
export function getModelData(
	pallet: Pallet_t,
	size: number,
	overhang: Layout_t['overhang']
): ModelData {
	const dimensions: PlaneDimensions = getPalletDimensions(pallet);
	const totalDimensions: PlaneDimensions = { ...dimensions };
	const overhangWidth = 3;
	const totalOverhang = overhang * 2;
	const totalOverhangWidth = overhangWidth * 2;

	const overhangWithBorder = totalOverhang + totalOverhangWidth;
	totalDimensions.width += overhangWithBorder;
	totalDimensions.length += overhangWithBorder;

	const norm = Math.sqrt(
		totalDimensions.width ** 2 + totalDimensions.length ** 2
	);

	let w = (totalDimensions.width / norm) * size;
	let l = (totalDimensions.length / norm) * size;
	// Scale up the coordinates to take maximum size.
	const scale = w >= l ? size / w : size / l;
	w *= scale;
	l *= scale;
	const cx = size / 2;
	const topX = cx - w / 2;
	const topY = (size - l) / 2;

	const properties = { w, l, norm, scale, cx, topX, topY, size };

	const widthOfPallet = (dimensions.width / norm) * size * scale;
	const lengthOfPallet = (dimensions.length / norm) * size * scale;

	const palletXOffset = (size - widthOfPallet) / 2;
	const palletYOffset = (size - lengthOfPallet) / 2;

	const widthWithOverhang =
		((dimensions.width + totalOverhang) / norm) * size * scale;
	const lengthWithOverhang =
		((dimensions.length + totalOverhang) / norm) * size * scale;

	const overhangBoxXOffset = (size - widthWithOverhang) / 2;
	const overhangBoxYOffset = (size - lengthWithOverhang) / 2;

	return {
		...properties,
		w: widthOfPallet,
		l: lengthOfPallet,
		topX: palletXOffset,
		topY: palletYOffset,
		overhangData: {
			width: widthWithOverhang,
			length: lengthWithOverhang,
			x: overhangBoxXOffset,
			y: overhangBoxYOffset,
		},
	};
}

export function getBestSnap(
	rect: ModelRect,
	snapLines: SnapLine[],
	snapStrength: number,
	allOtherBoxes: Rect[]
): {
	rect: ModelRect;
	xCandidate: SnapCandidate;
	yCandidate: SnapCandidate;
} | null {
	const xCandidates = proposeAxisSnaps(rect, 'x', snapLines, snapStrength);
	const yCandidates = proposeAxisSnaps(rect, 'y', snapLines, snapStrength);
	// Add a “no snap” fallback to each axis candidate list
	// so that we can also consider the possibility of snapping along X
	// but not Y, or vice versa.
	if (xCandidates.length === 0) {
		xCandidates.push({ pos: rect.x, distance: 0 });
	}
	if (yCandidates.length === 0) {
		yCandidates.push({ pos: rect.y, distance: 0 });
	}

	let bestDist = Infinity;
	let bestX = rect.x;
	let bestY = rect.y;
	let bestXCandidate = null;
	let bestYCandidate = null;

	for (const xc of xCandidates) {
		for (const yc of yCandidates) {
			const testRect = { ...rect, x: xc.pos, y: yc.pos };

			let isColliding = false;
			allOtherBoxes.forEach(other => {
				if (detectCollision(testRect, other)) {
					isColliding = true;
				}
			});
			if (!isColliding) {
				// Then compute how far we moved from the unsnapped newR
				const totalDist = Math.abs(xc.pos - rect.x) + Math.abs(yc.pos - rect.y);

				// Keep track of the smallest totalDist
				if (totalDist < bestDist) {
					bestDist = totalDist;
					bestX = xc.pos;
					bestY = yc.pos;
					bestXCandidate = xc;
					bestYCandidate = yc;
				}
			}
		}
	}

	// Decide whether to snap or move freely
	if (bestDist > snapStrength) {
		// No good snap within range => just move freely
		return null;
	}

	// Return our good snap
	return {
		rect: { ...rect, x: bestX, y: bestY },
		xCandidate: bestXCandidate as SnapCandidate,
		yCandidate: bestYCandidate as SnapCandidate,
	};
}

export function getFractionalCoordinates(
	modelData: ModelData,
	outerWidth: number,
	outerHeight: number,
	x: number,
	y: number
): [number, number] {
	const { topX, topY, size, w, l } = modelData;
	const palletX = (outerWidth - size) / 2 + topX;
	const palletY = topY;
	const fractionX = (x - palletX) / w;
	const fractionY = (y - palletY) / l;

	return [fractionX, fractionY];
}

export enum PALLETCORNERS {
	TOP_LEFT = 'TOP_LEFT',
	BOTTOM_LEFT = 'BOTTOM_LEFT',
	BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

export function IncreaseCorner(c: PALLETCORNERS) {
	if (c === PALLETCORNERS.TOP_LEFT) {
		return PALLETCORNERS.BOTTOM_LEFT;
	} else if (c === PALLETCORNERS.BOTTOM_LEFT) {
		return PALLETCORNERS.BOTTOM_RIGHT;
	} else {
		return PALLETCORNERS.BOTTOM_RIGHT;
	}
}

export function DecreaseCorner(c: PALLETCORNERS) {
	if (c === PALLETCORNERS.BOTTOM_LEFT) {
		return PALLETCORNERS.TOP_LEFT;
	} else if (c === PALLETCORNERS.BOTTOM_RIGHT) {
		return PALLETCORNERS.TOP_LEFT;
	} else {
		return PALLETCORNERS.TOP_LEFT;
	}
}

export function CornerNumber(c: PALLETCORNERS) {
	switch (c) {
		case PALLETCORNERS.TOP_LEFT: {
			return 0;
		}
		case PALLETCORNERS.BOTTOM_LEFT: {
			return 1;
		}
		case PALLETCORNERS.BOTTOM_RIGHT: {
			return 2;
		}
		default: {
			return -1;
		}
	}
}

interface SnapLine {
	position: number;
	orientation: 'vertical' | 'horizontal';
	source: 'pallet' | 'box';
	fromBox?: Rect; // if from box
	edge: 'left' | 'right' | 'top' | 'bottom';
}

export function computeSnapLines(
	boxes: Rect[],
	pallet: { xl: number; xh: number; yl: number; yh: number }
): SnapLine[] {
	const lines: SnapLine[] = [
		{
			position: pallet.xl,
			orientation: 'vertical',
			source: 'pallet',
			edge: 'left',
		},
		{
			position: pallet.xh,
			orientation: 'vertical',
			source: 'pallet',
			edge: 'right',
		},
		{
			position: pallet.yl,
			orientation: 'horizontal',
			source: 'pallet',
			edge: 'top',
		},
		{
			position: pallet.yh,
			orientation: 'horizontal',
			source: 'pallet',
			edge: 'bottom',
		},
	];
	for (const box of boxes) {
		lines.push(
			{
				position: box.x,
				orientation: 'vertical',
				source: 'box',
				fromBox: box,
				edge: 'left',
			},
			{
				position: box.x + box.width,
				orientation: 'vertical',
				source: 'box',
				fromBox: box,
				edge: 'right',
			},
			{
				position: box.y,
				orientation: 'horizontal',
				source: 'box',
				fromBox: box,
				edge: 'top',
			},
			{
				position: box.y + box.height,
				orientation: 'horizontal',
				source: 'box',
				fromBox: box,
				edge: 'bottom',
			}
		);
	}

	return lines;
}

export interface SnapCandidate {
	pos: number;
	distance: number;
	line: SnapLine;
}

export function proposeAxisSnaps(
	box: Rect,
	axis: 'x' | 'y',
	lines: SnapLine[],
	maxSnapDist: number
): { pos: number; distance: number; line?: SnapLine }[] {
	const candidates: SnapCandidate[] = [];
	// For X axis, consider box's left and right edges
	if (axis === 'x') {
		const boxLeft = box.x;
		const boxRight = box.x + box.width;
		const verticalLines = lines.filter(l => l.orientation === 'vertical');

		// For each vertical line, see if we can snap left or right edge
		for (const line of verticalLines) {
			// Check if line is within snap distance of either edge:
			const distLeft = Math.abs(line.position - boxLeft);
			const distRight = Math.abs(line.position - boxRight);

			// If snapping left edge to this line:
			if (distLeft <= maxSnapDist) {
				// Determine final pos for the box if we align the left edge
				let finalX = line.position;
				if (line.source === 'box') {
					finalX = line.position;
				}
				const candidate = { pos: finalX, distance: distLeft, line };
				candidates.push(candidate);
			}

			// If snapping right edge to this line:
			if (distRight <= maxSnapDist) {
				let finalX = line.position - box.width;
				if (line.source === 'box') {
					finalX = line.position - box.width;
				}
				const candidate = { pos: finalX, distance: distRight, line };
				candidates.push(candidate);
			}
		}
	} else {
		// For Y axis
		const boxTop = box.y;
		const boxBottom = box.y + box.height;
		const horizontalLines = lines.filter(l => l.orientation === 'horizontal');

		for (const line of horizontalLines) {
			const distTop = Math.abs(line.position - boxTop);
			const distBottom = Math.abs(line.position - boxBottom);

			// Snap top edge
			if (distTop <= maxSnapDist) {
				let finalY = line.position;
				if (line.source === 'box') {
					finalY = line.position;
				}
				candidates.push({ pos: finalY, distance: distTop, line });
			}

			// Snap bottom edge
			if (distBottom <= maxSnapDist) {
				let finalY = line.position - box.height;
				if (line.source === 'box') {
					finalY = line.position - box.height;
				}
				candidates.push({ pos: finalY, distance: distBottom, line });
			}
		}
	}

	return candidates;
}

export interface ModelBox extends BoxPosition_t {
	selected?: boolean;
}

function calculateBoxDimensions(
	isMultipickAlongWidth: boolean,
	multipickCount: number,
	baseDimensions: BoxDimensions,
	isRotated: boolean
): { width: number; height: number } {
	let { width, length } = baseDimensions;

	const multiplier = multipickCount + 1;
	if (isMultipickAlongWidth) {
		width *= multiplier;
	} else {
		length *= multiplier;
	}

	if (isRotated) {
		[width, length] = [length, width];
	}

	return { width, height: length };
}

interface Collider {
	x: number;
	y: number;
	width: number;
	height: number;
}

export function getCollidersFromBoxes(
	boxes: ModelBox[],
	boxDimensions: BoxDimensions,
	palletDimensions: PalletDimensions,
	gap: number = 0
): Collider[] {
	return boxes.map(box => {
		const { width, height } = calculateBoxDimensions(
			box.multipickAlongWidth,
			box.multipick,
			boxDimensions,
			box.rotated
		);
		const x = box.position.x * palletDimensions.width;
		const y = box.position.y * palletDimensions.length;
		return {
			x: x - gap / 2,
			y: y - gap / 2,
			width: width + gap,
			height: height + gap,
		};
	});
}

export function detectCollision(a: Collider, b: Collider): boolean {
	const tolerance = 0.05;

	const A_left = a.x;
	const A_right = a.x + a.width;
	const A_top = a.y;
	const A_bottom = a.y + a.height;

	const B_left = b.x;
	const B_right = b.x + b.width;
	const B_top = b.y;
	const B_bottom = b.y + b.height;

	const overlapX = Math.min(A_right, B_right) - Math.max(A_left, B_left);
	const overlapY = Math.min(A_bottom, B_bottom) - Math.max(A_top, B_top);

	return overlapX > tolerance && overlapY > tolerance;
}

export function detectAnyCollision(colliders: Collider[]): boolean {
	for (let i = 0; i < colliders.length; i++) {
		for (let j = i + 1; j < colliders.length; j++) {
			if (detectCollision(colliders[i], colliders[j])) {
				return true;
			}
		}
	}
	return false;
}

export function trySnapRectNearPosition(
	originalRect: ModelRect,
	allOtherBoxes: Rect[],
	snapLines: SnapLine[],
	maxCorrectionDist: number,
	dragBounds: LayoutBounds
): ModelRect | null {
	const newR = { ...originalRect };

	const xCandidates = proposeAxisSnaps(newR, 'x', snapLines, maxCorrectionDist);
	const yCandidates = proposeAxisSnaps(newR, 'y', snapLines, maxCorrectionDist);

	// “No-snap” fallback
	if (xCandidates.length === 0) {
		xCandidates.push({ pos: newR.x, distance: 0 });
	}
	if (yCandidates.length === 0) {
		yCandidates.push({ pos: newR.y, distance: 0 });
	}

	let bestRect: ModelRect | null = null;
	let bestDist = maxCorrectionDist;

	for (const xc of xCandidates) {
		for (const yc of yCandidates) {
			// “Candidate” final position
			const candidateRect = {
				...newR,
				x: xc.pos,
				y: yc.pos,
			};

			if (!isRectWithinBounds(candidateRect, dragBounds)) {
				continue; // skip
			}

			const dx = candidateRect.x - originalRect.x;
			const dy = candidateRect.y - originalRect.y;
			const dist = Math.sqrt(dx * dx + dy * dy);

			// Filter out candidates that overlap with other boxes
			const collides = allOtherBoxes.some((other, idx) =>
				detectCollision(candidateRect, other)
			);
			if (!collides) {
				// If we found a valid, non-overlapping candidate => see if it's the best (lowest dist)
				if (dist < bestDist) {
					bestDist = dist;
					bestRect = candidateRect;
				}
			}
		}
	}

	return bestRect;
}

const scaleDimensionsForPath = function (
	pPalletDims: PlaneDimensions,
	pBoxDimensions: BoxDimensions,
	pScale: number,
	overhang?: Layout_t['overhang']
): [PlaneDimensions, BoxDimensions] {
	const lPalletDims = { ...pPalletDims };
	const lBoxDims = { ...pBoxDimensions };
	const totalOverhang = overhang !== undefined ? overhang * 2 : 0;
	lPalletDims.length = (lPalletDims.length + totalOverhang) * pScale;
	lPalletDims.width = (lPalletDims.width + totalOverhang) * pScale;
	lBoxDims.length = lBoxDims.length * pScale;
	lBoxDims.width = lBoxDims.width * pScale;
	return [lPalletDims, lBoxDims];
};

const scalePackResult = function (
	pPackResult: PackResult,
	pScale: number
): PackResult {
	const lPackResult = { ...pPackResult };
	lPackResult.x *= pScale;
	lPackResult.y *= pScale;
	return lPackResult;
};

export async function generateAutoLayout(
	allPallets: Pallet_t[],
	allBoxes: Box_t[],
	box_index: number,
	pallet_index: number,
	gapDistance: number,
	t: LayoutTypes,
	pack: (
		palletLength: number,
		palletWidth: number,
		boxLength: number,
		boxWidth: number
	) => Promise<PackResult[]>,
	overhang: number
): Promise<BoxPosition_t[]> {
	const SAFETY_FACTOR = 0.01;
	const palletDims = getPalletDimensions(allPallets[pallet_index]);
	let boxDims = allBoxes[box_index].dimensions;
	boxDims.length += boxDims.length * SAFETY_FACTOR;
	boxDims.width += boxDims.width * SAFETY_FACTOR;
	const params: AutoLayerParams = {
		all_boxes: allBoxes,
		all_pallets: allPallets,
		box_index: box_index,
		pallet_index: pallet_index,
	};
	const ScalingFactor = 10000;
	switch (t) {
		case LayoutTypes.AllForward:
			return Promise.resolve(
				auto_all_forward(params, gapDistance, overhang).boxPositions
			);
		case LayoutTypes.AllRotated:
			return Promise.resolve(
				auto_all_rotated(params, gapDistance, overhang).boxPositions
			);
		case LayoutTypes.Optimized:
			const [lScaledPalletDims, lScaledBoxDims] = scaleDimensionsForPath(
				palletDims,
				boxDims,
				ScalingFactor,
				overhang
			);
			return pack(
				lScaledPalletDims.length,
				lScaledPalletDims.width,
				lScaledBoxDims.length,
				lScaledBoxDims.width
			).then((ps: PackResult[]) => {
				if (ps.length === 0) return [];
				const bpos = ps.map((p: PackResult) => {
					let { x, y, rotated } = scalePackResult(p, 1 / ScalingFactor);
					let width = rotated ? boxDims.length : boxDims.width,
						length = rotated ? boxDims.width : boxDims.length;
					x -= width / 2;
					x /= palletDims.width;
					y -= length / 2;
					y /= palletDims.length;
					return {
						position: { x, y },
						rotated,
						index: box_index,
						spin: false,
					} as BoxPosition_t;
				});
				const { x: cx, y: cy } = getBoxPositionsBoundingBoxCenter(
					allBoxes,
					bpos,
					1,
					allPallets[pallet_index].Dimensions
				);
				let dx = cx - 1 / 2,
					dy = cy - 1 / 2;
				bpos.forEach((bpo: BoxPosition_t) => {
					bpo.position.x -= dx;
					bpo.position.y -= dy;
				});
				return bpos;
			});
	}
}

/**
 * Checks whether a rectangle (2D box) is within given boundaries. The coordinate spaces of the two arguments must match.
 * @param rect The rectangle position and dimensions
 * @param dragBounds The boundaries to check
 * @returns true if the rectangle is within bounds and false otherwise
 */
export const isRectWithinBounds = (
	rect: Pick<Rect, 'x' | 'y' | 'width' | 'height'>,
	dragBounds: LayoutBounds,
	tolerance: number = 0
): boolean => {
	const rectTopLeft = {
		x: roundToNearestDecimal(rect.x, 1),
		y: roundToNearestDecimal(rect.y, 1),
	};
	const rectBottomRight = {
		x: roundToNearestDecimal(rect.x + rect.width, 1),
		y: roundToNearestDecimal(rect.y + rect.height, 1),
	};

	return (
		rectTopLeft.x + tolerance >= dragBounds.min.x &&
		rectTopLeft.y + tolerance >= dragBounds.min.y &&
		rectBottomRight.x - tolerance <= dragBounds.max.x &&
		rectBottomRight.y - tolerance <= dragBounds.max.y
	);
};

/**
 * Checks if a list of boxes in a layout are within the pallet and overhang bounds
 * @param layout The layout being edited
 * @param pallet The pallet being used in the layout
 * @param modelBoxes The boxes in the layout
 * @param boxes The list of boxes the user has configured
 * @returns true if no boxes are out of bounds and false otherwise
 */
export const areManyBoxesWithinBounds = (
	layout: Layout_t,
	pallet: Pallet_t,
	modelBoxes: ModelBox[],
	boxes: Box_t[]
) => {
	const totalOverhang =
		layout.overhang !== undefined ? layout.gapDistance / 2 + layout.overhang : 0;

	const bounds: LayoutBounds = {
		min: {
			x: roundToNearestDecimal(-totalOverhang, 0),
			y: roundToNearestDecimal(-totalOverhang, 0),
		},
		max: {
			x: roundToNearestDecimal(pallet.Dimensions.width + totalOverhang, 0),
			y: roundToNearestDecimal(pallet.Dimensions.length + totalOverhang, 0),
		},
	};

	return modelBoxes.some(box => {
		const { x, y } = box.position;
		const { width, length } = boxes[box.index].dimensions;

		// position of model boxes is in fractional coordinates (fraction of the real dimensions)
		const positionInMMWithGap = {
			x: roundToNearestDecimal(
				x * pallet.Dimensions.width - layout.gapDistance / 2,
				0
			),
			y: roundToNearestDecimal(
				y * pallet.Dimensions.length - layout.gapDistance / 2,
				0
			),
		};

		const { width: boxWidth, height: boxHeight } = calculateMultipickDimensions({
			width,
			height: length,
			...box,
		});

		return !isRectWithinBounds(
			{
				...positionInMMWithGap,
				width: roundToNearestDecimal(boxWidth + layout.gapDistance, 0),
				height: roundToNearestDecimal(boxHeight + layout.gapDistance, 0),
			},
			bounds,
			1
		);
	});
};

/**
 * Calculates a box's true width and height accounting for multipick and rotation
 * @param boxData The data required to recalculate dimensions
 * @returns The total width and height for a box accounting for multipick and rotation
 */
export function calculateMultipickDimensions(
	boxData: Pick<
		ModelRect,
		'width' | 'height' | 'multipick' | 'multipickAlongWidth' | 'rotated'
	>
) {
	const numberOfBoxes = (boxData.multipick ?? 0) + 1;
	let width = boxData.multipick
		? boxData.multipickAlongWidth
			? boxData.width * numberOfBoxes
			: boxData.width
		: boxData.width;
	let height = boxData.multipick
		? boxData.multipickAlongWidth
			? boxData.height
			: boxData.height * numberOfBoxes
		: boxData.height;

	return {
		width: boxData.rotated ? height : width,
		height: boxData.rotated ? width : height,
	};
}